import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  MapPin,
  RefreshCw,
  Bell,
  Users,
  Building2,
  Shield,
  Phone,
  Mail,
  Loader2,
  TrendingUp,
  AlertCircle
} from 'lucide-react';

export default function ShiftAssurancePage() {
  const { api, isAdmin } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [lateShifts, setLateShifts] = useState([]);
  const [siteCoverage, setSiteCoverage] = useState(null);
  const [alertHistory, setAlertHistory] = useState([]);
  const [triggeringAlerts, setTriggeringAlerts] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [summaryRes, lateRes, coverageRes, alertsRes] = await Promise.all([
        api.get('/shift-assurance/dashboard/summary'),
        api.get('/shift-assurance/late-shifts?minutes_threshold=10'),
        api.get('/shift-assurance/site-coverage'),
        api.get('/shift-assurance/alerts/history?days=7&limit=20')
      ]);
      
      setSummary(summaryRes.data);
      setLateShifts(lateRes.data || []);
      setSiteCoverage(coverageRes.data);
      setAlertHistory(alertsRes.data?.alerts || []);
      
    } catch (error) {
      console.error('Error fetching shift assurance data:', error);
      toast.error('Failed to load shift assurance data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleTriggerAlerts = async () => {
    setTriggeringAlerts(true);
    try {
      const response = await api.post('/shift-assurance/trigger-late-alerts?minutes_threshold=10');
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to trigger alerts');
    } finally {
      setTriggeringAlerts(false);
    }
  };

  const handleTriggerReminders = async () => {
    try {
      const response = await api.post('/shift-assurance/trigger-reminders');
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to trigger reminders');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'covered':
      case 'in_progress':
        return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50';
      case 'scheduled':
      case 'accepted':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 'late':
        return 'bg-amber-500/20 text-amber-400 border-amber-500/50';
      case 'uncovered':
      case 'missed':
        return 'bg-red-500/20 text-red-400 border-red-500/50';
      default:
        return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white flex items-center gap-2">
            <Shield className="w-7 h-7 text-blue-400" />
            SHIFT ASSURANCE
          </h1>
          <p className="text-zinc-400 text-sm">Monitor shift coverage, late check-ins, and site status</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchData}
            className="border-zinc-600"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {isAdmin && (
            <Button 
              variant="default"
              size="sm"
              onClick={handleTriggerAlerts}
              disabled={triggeringAlerts}
              className="bg-red-500 hover:bg-red-600"
            >
              {triggeringAlerts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
              Send Late Alerts
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase">Scheduled</p>
                <p className="text-2xl font-bold text-blue-400">{summary?.scheduled || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase">Accepted</p>
                <p className="text-2xl font-bold text-cyan-400">{summary?.accepted || 0}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-cyan-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase">Checked In</p>
                <p className="text-2xl font-bold text-emerald-400">{summary?.checked_in || 0}</p>
              </div>
              <Users className="w-8 h-8 text-emerald-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#18181b] border-[#3f3f46] border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-500 uppercase font-semibold">Late</p>
                <p className="text-2xl font-bold text-amber-400">{summary?.late || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#18181b] border-[#3f3f46] border-red-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-500 uppercase font-semibold">Missed</p>
                <p className="text-2xl font-bold text-red-400">{summary?.missed || 0}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase">Coverage</p>
                <p className="text-2xl font-bold text-white">{summary?.coverage_rate || 0}%</p>
              </div>
              <TrendingUp className="w-8 h-8 text-zinc-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Late Guards Alert Panel */}
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-rajdhani text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              LATE CHECK-INS
              {lateShifts.length > 0 && (
                <Badge className="bg-red-500/20 text-red-400 ml-2">{lateShifts.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {lateShifts.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-emerald-500/50" />
                <p>All guards checked in on time</p>
              </div>
            ) : (
              lateShifts.map((shift, idx) => (
                <div key={idx} className="p-3 bg-[#09090b] rounded border border-red-500/30">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-white">{shift.officer_name || 'Unknown Guard'}</p>
                      <p className="text-sm text-zinc-400 flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {shift.site_name}
                      </p>
                      <p className="text-xs text-zinc-500">
                        Shift started: {new Date(shift.start_time).toLocaleTimeString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/50">
                        {Math.round(shift.minutes_late)} min late
                      </Badge>
                      {shift.officer_phone && (
                        <a 
                          href={`tel:${shift.officer_phone}`}
                          className="flex items-center gap-1 text-xs text-blue-400 mt-2 hover:underline"
                        >
                          <Phone className="w-3 h-3" />
                          {shift.officer_phone}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Site Coverage Panel */}
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-rajdhani text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-400" />
              SITE COVERAGE
              <span className="text-sm font-normal text-zinc-400 ml-2">
                {siteCoverage?.covered || 0}/{siteCoverage?.total_sites || 0} covered
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {siteCoverage?.sites?.slice(0, 15).map((site, idx) => (
                <div 
                  key={idx} 
                  className={`p-2 rounded flex items-center justify-between ${
                    site.status === 'uncovered' ? 'bg-red-500/10 border border-red-500/30' :
                    site.status === 'covered' ? 'bg-emerald-500/10 border border-emerald-500/30' :
                    'bg-[#09090b] border border-[#3f3f46]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Building2 className={`w-4 h-4 ${
                      site.status === 'uncovered' ? 'text-red-400' :
                      site.status === 'covered' ? 'text-emerald-400' :
                      'text-zinc-400'
                    }`} />
                    <div>
                      <p className="text-sm font-medium text-white">{site.site_name}</p>
                      <p className="text-xs text-zinc-500">{site.site_code}</p>
                    </div>
                  </div>
                  <Badge className={getStatusColor(site.status)}>
                    {site.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alert History */}
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-rajdhani text-white flex items-center gap-2">
            <Bell className="w-5 h-5 text-yellow-400" />
            RECENT ALERTS
          </CardTitle>
        </CardHeader>
        <CardContent>
          {alertHistory.length === 0 ? (
            <div className="text-center py-6 text-zinc-500">
              <AlertCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No alerts in the last 7 days</p>
            </div>
          ) : (
            <div className="space-y-2">
              {alertHistory.map((alert, idx) => (
                <div key={idx} className="p-3 bg-[#09090b] rounded border border-[#3f3f46] flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {alert.type === 'late_check_in' ? (
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                    ) : (
                      <MapPin className="w-5 h-5 text-orange-400" />
                    )}
                    <div>
                      <p className="text-sm text-white">
                        {alert.type === 'late_check_in' 
                          ? `Late Check-In: ${alert.officer_name} at ${alert.site_name}`
                          : `Geofence Violation: ${alert.officer_name} (${alert.distance_m}m from site)`
                        }
                      </p>
                      <p className="text-xs text-zinc-500">
                        {new Date(alert.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <Badge className={alert.type === 'late_check_in' 
                    ? 'bg-amber-500/20 text-amber-400'
                    : 'bg-orange-500/20 text-orange-400'
                  }>
                    {alert.type === 'late_check_in' ? 'Late' : 'GPS'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
